// frontend/src/pages/TareaModal.jsx

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import tareasService from '../services/tareasService';
import kanbanService from '../services/kanbanService'; // 🆕 Para obtener estados del tablero
import presupuestoHierarchyService from '../services/presupuestoHierarchyService'; // Para obtener recursos del presupuesto

const TareaModal = ({ tarea, clientes, usuarios, tableros, proyectos, postId, clienteId, proyectoId, partidaPresupuestoId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    nombre_tarea: '',
    descripcion: '',
    cliente: clienteId ? String(clienteId) : '',
    proyecto: proyectoId ? String(proyectoId) : '',
    asignado_a: '',
    fecha_entrega: '',
    status: 'pending',
    tags: '',
    tablero: '',
    post: postId || '',
    recurso_presupuesto: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [timeLogs, setTimeLogs] = useState([]);
  const [loadingTimeLogs, setLoadingTimeLogs] = useState(false);
  const [tablerosFiltrados, setTablerosFiltrados] = useState([]);
  const [estatusTablero, setEstatusTablero] = useState([]); // 🆕 Estados del tablero seleccionado
  const [loadingEstatus, setLoadingEstatus] = useState(false); // 🆕 Indicador de carga de estados
  const [recursosPresupuesto, setRecursosPresupuesto] = useState([]); // Recursos del presupuesto
  const [loadingRecursos, setLoadingRecursos] = useState(false); // Indicador de carga de recursos

  // Debug
  console.log('Modal - Clientes recibidos:', clientes);
  console.log('Modal - Usuarios recibidos:', usuarios);
  console.log('Modal - Tableros recibidos:', tableros);
  console.log('Modal - Proyectos recibidos:', proyectos);
  console.log('Modal - PostId recibido:', postId);

  // Filtrar tableros según el usuario asignado
  useEffect(() => {
    if (!tableros || tableros.length === 0) {
      setTablerosFiltrados([]);
      return;
    }

    // Si no hay usuario asignado, mostrar todos los tableros
    if (!formData.asignado_a) {
      setTablerosFiltrados(tableros);
      return;
    }

    // Si hay usuario asignado, filtrar según permisos
    const usuarioId = parseInt(formData.asignado_a);
    const filtrados = tableros.filter(tablero => {
      // Si el tablero es público (no privado), incluirlo
      if (!tablero.is_private) {
        return true;
      }

      // Si el usuario es el propietario del tablero, incluirlo
      if (tablero.propietario === usuarioId) {
        return true;
      }

      // Si el tablero es privado, verificar si el usuario es miembro
      if (tablero.members && Array.isArray(tablero.members)) {
        return tablero.members.some(member => {
          // member puede ser un objeto con id o user_id
          const memberId = member.user?.id || member.user_id || member.id;
          return memberId === usuarioId;
        });
      }

      return false;
    });

    console.log('📋 Tableros filtrados para usuario', usuarioId, ':', filtrados);
    setTablerosFiltrados(filtrados);
  }, [formData.asignado_a, tableros]);

  useEffect(() => {
    if (tarea) {
      // Extraer el ID del usuario asignado correctamente
      let asignadoAId = '';
      if (tarea.asignado_a) {
        // Si asignado_a es un objeto con id, usar el id
        if (typeof tarea.asignado_a === 'object' && tarea.asignado_a.id) {
          asignadoAId = tarea.asignado_a.id;
        } else {
          // Si es solo un número, usarlo directamente
          asignadoAId = tarea.asignado_a;
        }
      }

      setFormData({
        nombre_tarea: tarea.nombre_tarea || '',
        descripcion: tarea.descripcion || '',
        cliente: tarea.cliente || '',
        proyecto: tarea.proyecto || '',
        asignado_a: asignadoAId,
        fecha_entrega: tarea.fecha_entrega || '',
        status: tarea.status || 'pending',
        tags: tarea.tags || '',
        tablero: tarea.tablero || '',
        post: tarea.post || postId || '',
        recurso_presupuesto: tarea.recurso_presupuesto || ''
      });

      // Cargar registros de tiempo
      loadTimeLogs(tarea.id);
    }
  }, [tarea]);

  // 🆕 Cargar estados del tablero cuando se selecciona uno
  useEffect(() => {
    if (formData.tablero) {
      loadBoardStatuses(formData.tablero);
    } else {
      // Si no hay tablero seleccionado, resetear los estados
      setEstatusTablero([]);
    }
  }, [formData.tablero]);

  // Cargar recursos del presupuesto cuando hay una partida seleccionada
  useEffect(() => {
    const loadRecursos = async () => {
      if (!partidaPresupuestoId) {
        setRecursosPresupuesto([]);
        return;
      }

      try {
        setLoadingRecursos(true);
        const response = await presupuestoHierarchyService.getCostoRecursos(partidaPresupuestoId);
        const recursosData = response.data?.results || response.data || [];
        setRecursosPresupuesto(Array.isArray(recursosData) ? recursosData : []);
        console.log('📋 Recursos del presupuesto cargados:', recursosData);
      } catch (error) {
        console.error('Error loading recursos:', error);
        setRecursosPresupuesto([]);
        toast.error('Error al cargar recursos del presupuesto');
      } finally {
        setLoadingRecursos(false);
      }
    };

    loadRecursos();
  }, [partidaPresupuestoId]);

  const loadTimeLogs = async (tareaId) => {
    try {
      setLoadingTimeLogs(true);
      const response = await tareasService.getTimeLogs(tareaId);
      console.log('Time logs response:', response);

      // Manejar diferentes formatos de respuesta
      let logs = [];
      if (Array.isArray(response)) {
        logs = response;
      } else if (response?.results && Array.isArray(response.results)) {
        logs = response.results;
      } else if (response?.data && Array.isArray(response.data)) {
        logs = response.data;
      } else if (response?.data?.results && Array.isArray(response.data.results)) {
        logs = response.data.results;
      }

      setTimeLogs(logs);
    } catch (error) {
      console.error('Error cargando registros de tiempo:', error);
      setTimeLogs([]); // Asegurarse de que siempre sea un array
    } finally {
      setLoadingTimeLogs(false);
    }
  };

  // 🆕 Cargar estados disponibles del tablero seleccionado
  const loadBoardStatuses = async (tableroId) => {
    try {
      setLoadingEstatus(true);
      console.log('📊 Cargando estados para el tablero:', tableroId);

      const response = await kanbanService.getBoard(tableroId);
      console.log('📊 Respuesta del tablero:', response);

      // Extraer estados del tablero
      let statuses = [];
      if (response?.estados && Array.isArray(response.estados)) {
        statuses = response.estados;
      } else if (response?.data?.estados && Array.isArray(response.data.estados)) {
        statuses = response.data.estados;
      } else if (response?.board?.estados && Array.isArray(response.board.estados)) {
        statuses = response.board.estados;
      }

      console.log('📊 Estados extraídos:', statuses);
      setEstatusTablero(statuses);
    } catch (error) {
      console.error('Error cargando estados del tablero:', error);
      setEstatusTablero([]); // Resetear a array vacío en caso de error
    } finally {
      setLoadingEstatus(false);
    }
  };

  // Formatear tiempo en HH:MM:SS
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Formatear fecha y hora
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validarFormulario = () => {
    const newErrors = {};

    if (!formData.nombre_tarea.trim()) {
      newErrors.nombre_tarea = 'El nombre de la tarea es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validarFormulario()) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      // Preparar datos para enviar (API de Kanban)
      const dataToSend = {
        nombre_tarea: formData.nombre_tarea,
        descripcion: formData.descripcion,
        tags: formData.tags,
        tablero: formData.tablero, // Tablero es requerido para crear la tarea
        status_personalizado: formData.status ? parseInt(formData.status) : null // 🆕 Convertir a número (ID del estado)
      };

      // Solo agregar campos opcionales si tienen valores
      if (formData.cliente) {
        dataToSend.cliente = formData.cliente;
      }
      if (formData.proyecto) {
        dataToSend.proyecto = formData.proyecto;
      }
      if (formData.asignado_a) {
        dataToSend.asignado_a = formData.asignado_a;
      }
      if (formData.fecha_entrega) {
        // El input type="date" ya devuelve YYYY-MM-DD
        dataToSend.fecha_entrega = formData.fecha_entrega;
      }
      if (formData.post) {
        dataToSend.post_asignado = formData.post;
      }
      if (formData.recurso_presupuesto) {
        dataToSend.recurso_presupuesto = formData.recurso_presupuesto;
      }

      console.log('📤 Datos a enviar:', dataToSend); // Debug log
      await onSave(dataToSend);
    } catch (error) {
      console.error('Error al guardar tarea:', error);

      // Manejar errores del backend
      if (error.details) {
        const backendErrors = {};
        Object.keys(error.details).forEach(key => {
          backendErrors[key] = error.details[key][0];
        });
        setErrors(backendErrors);
      }

      toast.error(error.error || 'Error al guardar tarea');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-l" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="modal-header-icon">{tarea ? '✏️' : '➕'}</div>
            <div className="modal-header-text">
              <h2 className="modal-header-title">{tarea ? 'Editar Tarea' : 'Nueva Tarea'}</h2>
              <p className="modal-header-subtitle">{tarea ? 'Actualiza los detalles de la tarea' : 'Crea una nueva tarea'}</p>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Formulario */}
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Nombre de la tarea */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Nombre de la Tarea <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              name="nombre_tarea"
              value={formData.nombre_tarea}
              onChange={handleInputChange}
              placeholder="Ej: Diseñar banner para campaña"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: errors.nombre_tarea ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            {errors.nombre_tarea && (
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                {errors.nombre_tarea}
              </p>
            )}
          </div>

          {/* Descripción */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Descripción
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              placeholder="Describe la tarea..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Cliente y Proyecto (en fila) - OCULTO pero se envía automáticamente */}
          <div style={{ display: 'none', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Cliente
              </label>
              <select
                name="cliente"
                value={formData.cliente}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Sin cliente</option>
                {clientes && clientes.length > 0 ? (
                  clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>{cliente.name}</option>
                  ))
                ) : (
                  <option disabled>No hay clientes disponibles</option>
                )}
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Proyecto
              </label>
              <select
                name="proyecto"
                value={formData.proyecto}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">Sin proyecto</option>
                {proyectos && proyectos.length > 0 ? (
                  proyectos.map(proyecto => (
                    <option key={proyecto.id} value={proyecto.id}>{proyecto.nombre}</option>
                  ))
                ) : (
                  <option disabled>No hay proyectos disponibles</option>
                )}
              </select>
            </div>
          </div>

          {/* Asignado a y Tablero (en fila) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Asignado a
              </label>
              <select
                name="asignado_a"
                value={formData.asignado_a}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Sin asignar</option>
                {usuarios && usuarios.length > 0 ? (
                  usuarios.map(usuario => (
                    <option key={usuario.id} value={usuario.id}>{usuario.full_name}</option>
                  ))
                ) : (
                  <option disabled>No hay usuarios disponibles</option>
                )}
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Tablero {formData.asignado_a && <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>(filtrado por usuario)</span>}
              </label>
              <select
                name="tablero"
                value={formData.tablero}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Selecciona un tablero</option>
                {tablerosFiltrados && tablerosFiltrados.length > 0 ? (
                  tablerosFiltrados.map(tablero => (
                    <option key={tablero.id} value={tablero.id}>
                      {tablero.nombre} {tablero.is_private ? '🔒' : ''}
                    </option>
                  ))
                ) : (
                  <option disabled>No hay tableros disponibles</option>
                )}
              </select>
            </div>
          </div>

          {/* Fecha de entrega y Estado (en fila) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Fecha de Entrega
              </label>
              <input
                type="date"
                name="fecha_entrega"
                value={formData.fecha_entrega}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Estado {loadingEstatus && <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>(cargando...)</span>}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={!formData.tablero || loadingEstatus}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: !formData.tablero ? '1px solid #e5e7eb' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  cursor: !formData.tablero || loadingEstatus ? 'not-allowed' : 'pointer',
                  boxSizing: 'border-box',
                  backgroundColor: !formData.tablero ? '#f9fafb' : 'white',
                  opacity: !formData.tablero ? 0.6 : 1
                }}
              >
                {!formData.tablero ? (
                  <option value="">Selecciona un tablero primero</option>
                ) : estatusTablero.length > 0 ? (
                  // 🆕 Renderizar estados dinámicos del tablero
                  estatusTablero.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.nombre || status.name || status.estado}
                    </option>
                  ))
                ) : (
                  <option value="">Sin estados disponibles</option>
                )}
              </select>
            </div>
          </div>

          {/* Recurso de Presupuesto */}
          {partidaPresupuestoId && (
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Recurso de Presupuesto
              </label>
              <select
                name="recurso_presupuesto"
                value={formData.recurso_presupuesto}
                onChange={handleInputChange}
                disabled={loadingRecursos}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  cursor: loadingRecursos ? 'not-allowed' : 'pointer',
                  boxSizing: 'border-box',
                  backgroundColor: loadingRecursos ? '#f3f4f6' : '#fff'
                }}
              >
                <option value="">
                  {loadingRecursos ? 'Cargando recursos...' : 'Selecciona un recurso (opcional)'}
                </option>
                {recursosPresupuesto && recursosPresupuesto.length > 0 ? (
                  recursosPresupuesto.map(recurso => (
                    <option key={recurso.id} value={recurso.id}>
                      {recurso.nombre} {recurso.descripcion ? `- ${recurso.descripcion}` : ''}
                    </option>
                  ))
                ) : (
                  !loadingRecursos && <option disabled>No hay recursos disponibles</option>
                )}
              </select>
            </div>
          )}

          {/* Tags */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Tags
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="diseño, urgente, campaña (separados por comas)"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <p style={{
              margin: '0.25rem 0 0 0',
              fontSize: '0.75rem',
              color: '#6b7280'
            }}>
              Separa los tags con comas
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ display: 'block' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="estado-btn"
            style={{
              display: 'inline-block',
              width: 'auto',
              marginRight: '0.5rem'
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
            style={{
              display: 'inline-block',
              width: 'auto'
            }}
          >
            {saving ? 'Guardando...' : (tarea ? 'Actualizar' : 'Crear Tarea')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TareaModal;
